import App from 'App';
import RequireAuth from 'components/auth/RequireAuth';
import AuthCallback from 'components/modules/auth/callback';
import MainLayout from 'layouts/MainLayout';

import ListConfig from 'pages/apps/platform/config/listConfig';
import ListConnections from 'pages/apps/platform/connections/listConnections';
import Documentation from 'pages/apps/platform/documentation/Documentation';
import ListRoles from 'pages/apps/platform/roles/listRoles';
import ListTables from 'pages/apps/platform/tables/listTables';
import ListUsers from 'pages/apps/platform/users/listUsers';
import ListWorkers from 'pages/apps/platform/workers/listWorkers';
import Error403 from 'pages/error/Error403';
import Error404 from 'pages/error/Error404';
import Error500 from 'pages/error/Error500';
import SimpleForgotPassword from 'pages/pages/authentication/simple/ForgotPassword';
import SimpleLockScreen from 'pages/pages/authentication/simple/LockScreen';
import SimpleResetPassword from 'pages/pages/authentication/simple/ResetPassword';
import SimpleSignIn from 'pages/pages/authentication/simple/SignIn';
import SimpleSignOut from 'pages/pages/authentication/simple/SignOut';
import SimpleSignUp from 'pages/pages/authentication/simple/SignUp';
import SimpleTwoFA from 'pages/pages/authentication/simple/TwoFA';
import MainLayoutProvider from 'providers/MainLayoutProvider';
import { RouteObject, createBrowserRouter } from 'react-router-dom';

import WorkflowDetailedView from 'pages/apps/platform/workflows/WorkflowDetailedView';
import ListAuditTrail from 'pages/apps/platform/audit/ListAuditTrail';
import ListCustomerNodeDefinitions from 'pages/apps/platform/customer_node_definitions/listCustomerNodeDefinitions';
import NodeDefinitionEditor from 'pages/apps/platform/customer_node_definitions/node_definition_editor/NodeDefinitionEditor';
import NodeInstances from 'pages/apps/platform/list_node_instances/nodeInstances';
import FileEventInstances from 'pages/apps/platform/file_event_instances/FileEventInstances';
import EventInstances from 'pages/apps/platform/event_instances/EventInstances';
import CreateNodeDefinitionEditor from 'pages/apps/platform/customer_node_definitions/node_definition_editor/CreateNodeDefinitionEditor';
import ListQueues from 'pages/apps/platform/queues/listQueues';
import InstanceViewerWithTree from 'pages/apps/platform/customer_node_definitions/node_definition_editor/instance_tree/InstanceViewerWithTree';

import ListEvents from 'pages/apps/platform/events/listEvents';
import ListReportDefinitions from 'pages/apps/platform/reports/listReportDefinitions';
import ReportRunner from 'pages/apps/platform/reports/ReportRunner';
import ListZones from 'pages/apps/platform/zones/listZones';
import ListFolders from 'pages/apps/platform/folders/listFolders';
import WorkspacePage from 'pages/apps/platform/workspace/WorkspacePage';
import ListIntegrations from 'pages/apps/platform/integrations/ListIntegrations';
import EditIntegration from 'pages/apps/platform/integrations/EditIntegration';
import ListIntegrationNodes from 'pages/apps/platform/integrations/ListIntegrationNodes';
import EditIntegrationNode from 'pages/apps/platform/integrations/EditIntegrationNode';
import ListLocks from 'pages/apps/platform/locks/listLocks';
import ListPythonLibraries from 'pages/apps/platform/python_libraries/ListPythonLibraries';

import { ReactFlowProvider } from 'reactflow';
import NodeSubmit from 'pages/apps/platform/submit/NodeSubmit';
import UpdateNodeSubmit from 'pages/apps/platform/update_submit/UpdateNodeSubmit';
import Homepage from 'pages/apps/platform/homepage';
import ListSchedulingWindows from 'pages/apps/platform/scheduling_window/SchedulingWindow';
import ListRecurrenceDefinitions from 'pages/apps/platform/recurrence_definitions/RecurrenceDefinitions';
import NodeInstanceFileViewer from 'pages/apps/platform/list_node_instances/NodeInstanceFileViewer';
import DocPage from 'pages/apps/platform/documentation/DocPage';
import ListTriggers from 'pages/apps/platform/triggers/listTriggers';
import ListMonitoringDashboards from 'pages/apps/platform/monitoring/dashboards/ListMonitoringDashboards';
import MonitoringDashboardEditor from 'pages/apps/platform/monitoring/dashboards/MonitoringDashboardEditor';
import MonitoringDashboardViewer from 'pages/apps/platform/monitoring/dashboards/MonitoringDashboardViewer';
import ListTransports from 'pages/apps/platform/transports/ListTransports';
import ListImports from 'pages/apps/platform/imports/ListImports';

const routes: RouteObject[] = [
  {
    element: <App />,
    children: [
      {
        path: '/',
        element: (
          // <Provider store={store}>
          <MainLayoutProvider>
            <MainLayout />
          </MainLayoutProvider>
          // </Provider>
        ),
        children: [
          {
            index: true,
            element: (
              <MainLayoutProvider>
                <Homepage />
              </MainLayoutProvider>
            ),
          },
          {
            path: '/documentation',
            element: (
              <MainLayoutProvider>
                <Documentation />
              </MainLayoutProvider>
            ),
            children: [
              { index: true, element: <DocPage /> },
              { path: ':slug*', element: <DocPage /> },
            ],
          },
          {
            path: 'inbox/:workflowId?',
            element: (
              <MainLayoutProvider>
                <WorkflowDetailedView />
              </MainLayoutProvider>
            ),
          },
          {
            path: '/audittrail',
            element: <ListAuditTrail />,
          },
          {
            path: '/configuration/',
            children: [
              {
                path: 'users',
                element: <ListUsers />,
              },
              {
                path: 'roles',
                element: <ListRoles />,
              },
              {
                path: 'configitems',
                element: <ListConfig />,
              },
              {
                path: 'tables',
                element: <ListTables />,
              },
              {
                path: 'connections',
                element: <ListConnections />,
              },
              {
                path: 'workers',
                element: <ListWorkers />,
              },
              {
                path: 'queues',
                element: <ListQueues />,
              },
              {
                path: 'zones',
                element: <ListZones />,
              },
              {
                path: 'folders',
                element: <ListFolders />,
              },
              {
                path: 'integrations',
                element: <ListIntegrations />,
              },
              {
                path: 'integrations/new',
                element: <EditIntegration />,
              },
              {
                path: 'integrations/:integrationId/edit',
                element: <EditIntegration />,
              },
              {
                path: 'integrations/:integrationId/nodes',
                element: <ListIntegrationNodes />,
              },
              {
                path: 'integrations/:integrationId/nodes/new',
                element: <EditIntegrationNode />,
              },
              {
                path: 'integrations/:integrationId/nodes/:nodeId/edit',
                element: <EditIntegrationNode />,
              },
              {
                path: 'locks',
                element: <ListLocks />,
              },
              {
                path: 'recurrence_definitions',
                element: <ListRecurrenceDefinitions />,
              },
              {
                path: 'pythonlibraries',
                element: <ListPythonLibraries />,
              },
            ],
          },
          {
            path: '/automation/',
            children: [
              {
                path: 'node_definitions',
                element: <ListCustomerNodeDefinitions />,
              },
              {
                path: 'events',
                element: <ListEvents />,
              },
              {
                path: 'node_instances',
                element: <NodeInstances />,
              },
              {
                path: 'scheduling_window',
                element: <ListSchedulingWindows />,
              },
              {
                path: 'file_event_instances/:zone_name/:name',
                element: <FileEventInstances />,
              },
              {
                path: 'event_instances/:zone_name/:name',
                element: <EventInstances />,
              },
              {
                path: 'triggers',
                element: <ListTriggers />,
              },
            ],
          },
          {
            path: '/reports/',
            children: [
              {
                path: 'definitions',
                element: <ListReportDefinitions />,
              },
              {
                path: 'run/:rd_id',
                element: <ReportRunner />,
              },
            ],
          },
          {
            path: '/workspace',
            element: <WorkspacePage />,
          },
          {
            path: '/transports',
            element: <ListTransports />,
          },
          {
            path: '/imports',
            element: <ListImports />,
          },
          {
            path: '/monitoring/',
            children: [
              {
                path: 'dashboards',
                element: <ListMonitoringDashboards />,
              },
              {
                path: 'dashboards/new',
                element: (
                  <ReactFlowProvider>
                    <MonitoringDashboardEditor />
                  </ReactFlowProvider>
                ),
              },
              {
                path: 'dashboards/:dashboardId/edit',
                element: (
                  <ReactFlowProvider>
                    <MonitoringDashboardEditor />
                  </ReactFlowProvider>
                ),
              },
              {
                path: 'dashboards/:dashboardId/view',
                element: (
                  <ReactFlowProvider>
                    <MonitoringDashboardViewer />
                  </ReactFlowProvider>
                ),
              },
            ],
          },
        ],
      },
      {
        path: 'node_editor/:nodeId/:name',
        element: (
          <RequireAuth>
            <ReactFlowProvider>
              <NodeDefinitionEditor />
            </ReactFlowProvider>
          </RequireAuth>
        ),
      },
      {
        path: 'instance_viewer/:nodeInstanceId',
        element: (
          <RequireAuth>
            <ReactFlowProvider>
              <InstanceViewerWithTree />
            </ReactFlowProvider>
          </RequireAuth>
        ),
      },
      {
        path: 'node_instance_file/:nodeInstanceId/:fileName',
        element: (
          <RequireAuth>
            <NodeInstanceFileViewer />
          </RequireAuth>
        ),
      },
      {
        path: 'node_editor',
        element: (
          <RequireAuth>
            <CreateNodeDefinitionEditor />
          </RequireAuth>
        ),
      },
      {
        path: 'node_submit/:zone_name/:name',
        element: (
          <RequireAuth>
            <ReactFlowProvider>
              <NodeSubmit />
            </ReactFlowProvider>
          </RequireAuth>
        ),
      },
      {
        path: 'node_update_instance/:zone_name/:name/:instance_id',
        element: (
          <RequireAuth>
            <ReactFlowProvider>
              <UpdateNodeSubmit />
            </ReactFlowProvider>
          </RequireAuth>
        ),
      },
      {
        path: 'node_resubmit/:zone_name/:name/:instance_id',
        element: (
          <RequireAuth>
            <ReactFlowProvider>
              <NodeSubmit />
            </ReactFlowProvider>
          </RequireAuth>
        ),
      },
      {
        path: '/login',
        element: <SimpleSignIn />,
      },
      {
        path: '/callback',
        element: <AuthCallback />,
      },
      {
        path: 'logoff',
        element: <SimpleSignOut />,
      },
      {
        path: '/pages/errors/',
        children: [
          {
            path: '404',
            element: <Error404 />,
          },
          {
            path: '403',
            element: <Error403 />,
          },
          {
            path: '500',
            element: <Error500 />,
          },
        ],
      },
      {
        path: '/pages/authentication/simple/',
        children: [
          {
            path: 'sign-in',
            element: <SimpleSignIn />,
          },
          {
            path: 'sign-up',
            element: <SimpleSignUp />,
          },
          {
            path: 'sign-out',
            element: <SimpleSignOut />,
          },
          {
            path: 'forgot-password',
            element: <SimpleForgotPassword />,
          },
          {
            path: 'reset-password',
            element: <SimpleResetPassword />,
          },
          {
            path: 'lock-screen',
            element: <SimpleLockScreen />,
          },
          {
            path: '2FA',
            element: <SimpleTwoFA />,
          },
        ],
      },
      {
        path: '*',
        element: <Error404 />,
      },
    ],
  },
];

export const router = createBrowserRouter(routes);

export default routes;
